import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import SelectDropdown, { type SelectOption } from "@/components/ui/select-dropdown";
import {
  ALERT_AUDIENCE_LABELS,
  ALERT_CATEGORY_LABELS,
  type AlertAudience,
  type AlertCategory,
} from "@/services/api/guardiansv2/types";
import type { AlertAudienceFilter, AlertCategoryFilter, GuardiansFilterValues } from "./GuardiansFilterBar";
import type { AuditOriginFilter, GuardiansView } from "../guardiansParams";
import type { AuditBucket } from "../audits/useAudits";
import type { ScreeningBucket } from "../screenings/useScreenings";
import {
  AUDIT_BUCKET_FILTER_OPTIONS,
  AUDIT_ORIGIN_FILTER_OPTIONS,
  AUDIT_TYPE_FILTER_OPTIONS,
  SCREENING_BUCKET_FILTER_OPTIONS,
  guardiansActiveFilterCount,
  type GuardiansTabFilterState,
} from "./guardiansViewFilters";
import { isPortaledListboxInteraction } from "./guardiansPopoverDismiss";

const DROPDOWN_W = "min-w-[var(--anchor-width)] w-max max-w-sm";

/** Overrides SelectDropdown defaults (h-10, rounded-xl, light bg). */
const SELECT_BUTTON =
  "!h-8 !rounded-lg !border-0 !bg-slate-900/80 !py-0 !pl-2.5 !pr-7 !text-left !text-xs !font-normal !text-slate-200 !shadow-none ring-1 ring-inset ring-white/10 hover:!bg-slate-800 focus:!outline-none focus-visible:!ring-2 focus-visible:!ring-indigo-500/40";

const DROPDOWN_PANEL =
  "!rounded-lg !border !border-white/10 !bg-slate-800 !p-1 !text-xs !shadow-xl !ring-1 !ring-white/5 [--anchor-gap:0.25rem]";

const SELECT_WRAPPER = "min-w-0 [&>div]:!mt-0";

const CATEGORY_OPTIONS: SelectOption<AlertCategoryFilter>[] = [
  { value: "all", label: "All categories" },
  ...(Object.entries(ALERT_CATEGORY_LABELS) as Array<[AlertCategory, string]>).map(([value, label]) => ({
    value,
    label,
  })),
];

const AUDIENCE_OPTIONS: SelectOption<AlertAudienceFilter>[] = [
  { value: "all", label: "All audiences" },
  ...(Object.entries(ALERT_AUDIENCE_LABELS) as Array<[AlertAudience, string]>).map(([value, label]) => ({
    value,
    label,
  })),
];

function tabFilterSectionTitle(view: GuardiansView): string | null {
  if (view === "alerts") return "Alerts";
  if (view === "audits") return "Audits";
  if (view === "screenings") return "Screenings";
  return null;
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
      {children}
    </div>
  );
}

const SECTION_TITLE_ROW = "mb-2 flex min-h-7 items-center gap-2";

function SectionTitle({
  title,
  subdued,
  actions,
}: {
  title: string;
  subdued?: boolean;
  actions?: ReactNode;
}) {
  return (
    <div className={`${SECTION_TITLE_ROW} ${actions ? "justify-between" : ""}`}>
      <h3
        className={
          subdued
            ? "text-[10px] font-semibold uppercase tracking-wide text-indigo-300/90"
            : "text-[10px] font-semibold uppercase tracking-wide text-slate-400"
        }
      >
        {title}
      </h3>
      {actions}
    </div>
  );
}

type GuardiansFilterSelectProps<T> = {
  options: SelectOption<T>[];
  value: T;
  onChange: (value: T) => void;
  "data-testid"?: string;
};

function GuardiansFilterSelect<T>({ options, value, onChange, "data-testid": dataTestId }: GuardiansFilterSelectProps<T>) {
  return (
    <SelectDropdown
      options={options}
      value={value}
      onChange={onChange}
      usePortal
      className={SELECT_WRAPPER}
      buttonClassName={SELECT_BUTTON}
      dropdownClassName={DROPDOWN_PANEL}
      dropdownWidth={DROPDOWN_W}
      data-testid={dataTestId}
    />
  );
}

function PopoverActions({
  canReset,
  onReset,
  onClose,
}: {
  canReset: boolean;
  onReset: () => void;
  onClose: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      {canReset ? (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-slate-400 hover:bg-white/5 hover:text-slate-200"
          data-testid="guardians-filters-clear"
        >
          <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
          <span>Clear</span>
        </button>
      ) : null}
      <button
        type="button"
        onClick={onClose}
        className="rounded-md p-1 text-slate-400 hover:bg-white/5"
        aria-label="Close filters"
      >
        <FontAwesomeIcon icon={faXmark} className="text-sm" />
      </button>
    </div>
  );
}

export interface GuardiansMoreFiltersPopoverProps {
  view: GuardiansView;
  values: GuardiansFilterValues;
  tabFilters: GuardiansTabFilterState;
  missionTemplates: SelectOption<string>[];
  agents: SelectOption<string>[];
  catalogues: SelectOption<string>[];
  onChange: (next: GuardiansFilterValues) => void;
  onAuditBucketChange: (bucket: AuditBucket) => void;
  onAuditOriginChange: (origin: AuditOriginFilter) => void;
  onScreeningBucketChange: (bucket: ScreeningBucket) => void;
  onReset: () => void;
  canReset: boolean;
  onClose: () => void;
  anchorRef?: RefObject<HTMLElement | null>;
}

export function GuardiansMoreFiltersPopover({
  view,
  values,
  tabFilters,
  missionTemplates,
  agents,
  catalogues,
  onChange,
  onAuditBucketChange,
  onAuditOriginChange,
  onScreeningBucketChange,
  onReset,
  canReset,
  onClose,
  anchorRef,
}: GuardiansMoreFiltersPopoverProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      if (isPortaledListboxInteraction(target)) return;
      onClose();
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEscape);
    };
  }, [onClose, anchorRef]);

  const applyValues = (next: GuardiansFilterValues) => {
    onChange(next);
    onClose();
  };

  const showScope = view === "alerts" || view === "audits" || view === "screenings";
  const showAlertFilters = view === "alerts";
  const showAuditFilters = view === "audits";
  const showScreeningFilters = view === "screenings";
  const tabTitle = tabFilterSectionTitle(view);
  const hasTabSection = showAlertFilters || showAuditFilters || showScreeningFilters;

  const handleReset = () => {
    onReset();
    onClose();
  };

  return (
    <div
      ref={containerRef}
      className="w-[min(36rem,calc(100vw-2rem))] rounded-xl border border-white/10 bg-slate-800/95 p-3 shadow-xl ring-1 ring-white/5 backdrop-blur-sm"
      data-testid="guardians-more-filters-popover"
      data-guardians-filter-view={view}
    >
      {showScope ? (
        <section data-testid="guardians-filter-section-scope">
          <SectionTitle
            title="Scope"
            actions={<PopoverActions canReset={canReset} onReset={handleReset} onClose={onClose} />}
          />
          <div className="grid grid-cols-3 gap-2">
            <FilterField label="Mission">
              <GuardiansFilterSelect
                options={missionTemplates}
                value={values.missionTemplateId}
                onChange={(missionTemplateId) =>
                  applyValues({ ...values, missionTemplateId })
                }
                data-testid="guardians-filter-mission-template"
              />
            </FilterField>
            <FilterField label="Agent">
              <GuardiansFilterSelect
                options={agents}
                value={values.agentId}
                onChange={(agentId) => applyValues({ ...values, agentId })}
                data-testid="guardians-filter-agent"
              />
            </FilterField>
            <FilterField label="Catalogue">
              <GuardiansFilterSelect
                options={catalogues}
                value={values.catalogueId}
                onChange={(catalogueId) => applyValues({ ...values, catalogueId })}
                data-testid="guardians-filter-catalogue"
              />
            </FilterField>
          </div>
        </section>
      ) : null}

      {hasTabSection && tabTitle ? (
        <div
          className={showScope ? "mt-5 border-t border-white/10 pt-5" : undefined}
          data-testid="guardians-filter-tab-section"
        >
          {!showScope ? (
            <div className="mb-2 flex items-center justify-end border-b border-white/5 pb-2">
              <PopoverActions canReset={canReset} onReset={handleReset} onClose={onClose} />
            </div>
          ) : null}
          <SectionTitle title={tabTitle} subdued />
          <div
            className={
              showAuditFilters
                ? "grid grid-cols-3 gap-2"
                : "grid grid-cols-2 gap-2"
            }
          >
            {showAlertFilters ? (
              <>
                <FilterField label="Category">
                  <GuardiansFilterSelect
                    options={CATEGORY_OPTIONS}
                    value={values.alertCategory}
                    onChange={(alertCategory) =>
                      applyValues({
                        ...values,
                        alertCategory: alertCategory as AlertCategoryFilter,
                      })
                    }
                    data-testid="guardians-filter-category"
                  />
                </FilterField>
                <FilterField label="Audience">
                  <GuardiansFilterSelect
                    options={AUDIENCE_OPTIONS}
                    value={values.alertAudience}
                    onChange={(alertAudience) =>
                      applyValues({
                        ...values,
                        alertAudience: alertAudience as AlertAudienceFilter,
                      })
                    }
                    data-testid="guardians-filter-audience"
                  />
                </FilterField>
              </>
            ) : null}
            {showAuditFilters ? (
              <>
                <FilterField label="Audit type">
                  <GuardiansFilterSelect
                    options={AUDIT_TYPE_FILTER_OPTIONS}
                    value={values.auditType}
                    onChange={(auditType) => applyValues({ ...values, auditType })}
                    data-testid="guardians-filter-audit-type"
                  />
                </FilterField>
                <FilterField label="Origin">
                  <GuardiansFilterSelect
                    options={AUDIT_ORIGIN_FILTER_OPTIONS}
                    value={tabFilters.auditOrigin}
                    onChange={(origin) => {
                      onAuditOriginChange(origin);
                      onClose();
                    }}
                    data-testid="guardians-filter-audit-origin"
                  />
                </FilterField>
                <FilterField label="Status">
                  <GuardiansFilterSelect
                    options={AUDIT_BUCKET_FILTER_OPTIONS}
                    value={tabFilters.auditBucket}
                    onChange={(bucket) => {
                      onAuditBucketChange(bucket);
                      onClose();
                    }}
                    data-testid="guardians-filter-audit-status"
                  />
                </FilterField>
              </>
            ) : null}
            {showScreeningFilters ? (
              <FilterField label="Outcome">
                <GuardiansFilterSelect
                  options={SCREENING_BUCKET_FILTER_OPTIONS}
                  value={tabFilters.screeningBucket}
                  onChange={(bucket) => {
                    onScreeningBucketChange(bucket);
                    onClose();
                  }}
                  data-testid="guardians-filter-screening-outcome"
                />
              </FilterField>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function popoverCanReset(
  view: GuardiansView,
  values: GuardiansFilterValues,
  tabFilters: GuardiansTabFilterState
): boolean {
  return guardiansActiveFilterCount(view, values, tabFilters) > 0;
}
